<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingRules',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getContainer = (...args) => {
    return {
      self: $fn.value.urlFor('create_object_by_type'),
      object: 'list',
      data: [],
    };
  };
  const getPayload = (...args) => {
    const [editor] = args;
    const exclude = [];

    return editor.isNew
      ? editor.getData({ exclude })
      : editor.getChanges({ exclude, skipEmbeddedLists: true });
  };

  const formId = ref(null);
  const formName = ref('billingListItem');
  const items = ref(null);
  const selectedItem = ref(null);
  const createItem = ref(false);
  const editingItemID = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_1040167445267876_style = computed(() => ({
    minHeight: '300px',
  }));
  const b_8527427120829392_style = computed(() => ({
    minWidth: '20px',
  }));
  const b_8363486085232621_style = computed(() => ({
    minWidth: '20px',
  }));
  const b_4186377094414664_style = computed(() => ({
    minHeight: '300px',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    items: items.value,
    selectedItem: selectedItem.value,
    createItem: createItem.value,
    editingItemID: editingItemID.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_1040167445267876_style: b_1040167445267876_style.value,
    b_8527427120829392_style: b_8527427120829392_style.value,
    b_8363486085232621_style: b_8363486085232621_style.value,
    b_4186377094414664_style: b_4186377094414664_style.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_items = () => {
    return $fn.value.fetch('list_object_by_type', {
      parameters: { object_type: 'billing-rule' },
      asResource: true,
    });
  };
  const $$load_selectedItem = () => {
    return items.value.length ? items.value[0] : null;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'items' },
      { name: 'selectedItem', dependsOn: ['items'] },
      { name: 'createItem' },
      { name: 'editingItemID' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_1040167445267876_style', nodeId: '1040167445267876' },
      { name: 'b_8527427120829392_style', nodeId: '8527427120829392' },
      { name: 'b_8363486085232621_style', nodeId: '8363486085232621' },
      { name: 'b_4186377094414664_style', nodeId: '4186377094414664' },
    ],
    loaders: {
      formId: $$load_formId,
      items: $$load_items,
      selectedItem: $$load_selectedItem,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      items,
      selectedItem,
      createItem,
      editingItemID,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_1040167445267876_style,
      b_8527427120829392_style,
      b_8363486085232621_style,
      b_4186377094414664_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext('BillingRules|Title', 'Billing Rules'),
          '(' + (items.value ? items.value.length : 0) + ')',
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_m5q9p7mpa_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2095089031469157_showModal_6dxbjchcn_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'FileSelector',
      props: {
        url: $fn.value.urlFor('import_object_by_type', {
          object_type: 'billing-rule',
        }),
        accept: '.json',
      },
    };
  };
  const act_5105091150304644_script_dlemt9hxc_value_callable = async (
    $event
  ) => {
    return (setData('createItem', null), setData('editingItemID', null));
  };
  const act_5105091150304644_script_dlemt9hxc_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5105091150304644_script_dlemt9hxc_value_callable($event);
      },
    };
  };
  const act_5105091150304644_script_5qiz2prkb_value_callable = async (
    $event
  ) => {
    return (setData('createItem', null), setData('editingItemID', null));
  };
  const act_5105091150304644_script_5qiz2prkb_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5105091150304644_script_5qiz2prkb_value_callable($event);
      },
    };
  };
  const act_8387648034966287_script_hyttt3iky_value_callable = async (
    $event
  ) => {
    return (editingItemID.value = $event.scope.item.id);
  };
  const act_8387648034966287_script_hyttt3iky_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8387648034966287_script_hyttt3iky_value_callable($event);
      },
    };
  };
  const act_6964713724626057_rest_7z5mjgwb9_getActionArgs = async ($event) => {
    return {
      method: 'delete',
      resource: $event.scope.item,
    };
  };
  const act_2272469684735879_script_q4571ujxc_value_callable = async (
    $event
  ) => {
    return (setData('createItem', null), setData('editingItemID', null));
  };
  const act_2272469684735879_script_q4571ujxc_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2272469684735879_script_q4571ujxc_value_callable($event);
      },
    };
  };
  const act_2272469684735879_script_q44btt2ru_value_callable = async (
    $event
  ) => {
    return (setData('createItem', null), setData('editingItemID', null));
  };
  const act_2272469684735879_script_q44btt2ru_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2272469684735879_script_q44btt2ru_value_callable($event);
      },
    };
  };
  const act_3373384577795494_script_sd8l4uts7_value_callable = async (
    $event
  ) => {
    return (createItem.value = true);
  };
  const act_3373384577795494_script_sd8l4uts7_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3373384577795494_script_sd8l4uts7_value_callable($event);
      },
    };
  };
  const act_3601807637515735_script_6dfyybgot_value_callable = async (
    $event
  ) => {
    return (createItem.value = true);
  };
  const act_3601807637515735_script_6dfyybgot_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3601807637515735_script_6dfyybgot_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_m5q9p7mpa =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_m5q9p7mpa_getActionArgs,
    });
  const inst_evh_7315092382398562_close_nkthxwh0g =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_m5q9p7mpa,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_nkthxwh0g],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_nkk5wwru4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_it2v20k6y =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_nkk5wwru4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_it2v20k6y],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_kwylv8vcf =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_0l2r8kdhg =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_kwylv8vcf,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_0l2r8kdhg],
    event: 'close',
  });
  const act_4615685607825208_reloadComponentData_6c0bvpyvn =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_b0k3muiq9 =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_6c0bvpyvn,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_b0k3muiq9],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_reloadComponentData_t86lk5c4j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2095089031469157_close_5gk0cpm0a =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadComponentData_t86lk5c4j,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_2095089031469157_showModal_6dxbjchcn =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_6dxbjchcn_getActionArgs,
      events: [inst_evh_2095089031469157_close_5gk0cpm0a],
    });
  const inst_evh_2095089031469157_clickPreventStop_8qrjrvimo =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_6dxbjchcn,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_8qrjrvimo],
      event: 'click.prevent.stop',
    });
  const act_5105091150304644_script_dlemt9hxc = new actionTypes.ScriptAction({
    actionArgs: act_5105091150304644_script_dlemt9hxc_getActionArgs,
  });
  const inst_evh_5105091150304644_saved_isf98dl0y =
    new actionTypes.EventHandlerImpl({
      action: act_5105091150304644_script_dlemt9hxc,
      event: 'saved',
      displayName: 'script',
    });
  const act_5105091150304644_reloadComponentData_xmerhotqa =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5105091150304644_saved_k404kv0mo =
    new actionTypes.EventHandlerImpl({
      action: act_5105091150304644_reloadComponentData_xmerhotqa,
      event: 'saved',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5105091150304644_saved = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_5105091150304644_saved_isf98dl0y,
      inst_evh_5105091150304644_saved_k404kv0mo,
    ],
    event: 'saved',
  });
  const act_5105091150304644_script_5qiz2prkb = new actionTypes.ScriptAction({
    actionArgs: act_5105091150304644_script_5qiz2prkb_getActionArgs,
  });
  const inst_evh_5105091150304644_cancelEdit_7lfryjrrx =
    new actionTypes.EventHandlerImpl({
      action: act_5105091150304644_script_5qiz2prkb,
      event: 'cancelEdit',
      displayName: 'script',
    });
  const inst_evh_5105091150304644_cancelEdit =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5105091150304644_cancelEdit_7lfryjrrx],
      event: 'cancelEdit',
    });
  const act_8387648034966287_script_hyttt3iky = new actionTypes.ScriptAction({
    actionArgs: act_8387648034966287_script_hyttt3iky_getActionArgs,
    displayName: 'editItem',
  });
  const inst_evh_8387648034966287_clickPreventStop_svfhbvb3y =
    new actionTypes.EventHandlerImpl({
      action: act_8387648034966287_script_hyttt3iky,
      event: 'click.prevent.stop',
      displayName: 'editItem',
    });
  const inst_evh_8387648034966287_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8387648034966287_clickPreventStop_svfhbvb3y],
      event: 'click.prevent.stop',
    });
  const act_6964713724626057_reloadComponentData_e1h0oj02p =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6964713724626057_deleted_auxyt6ulv =
    new actionTypes.EventHandlerImpl({
      action: act_6964713724626057_reloadComponentData_e1h0oj02p,
      event: 'deleted',
      displayName: 'reloadComponentData',
    });
  const act_6964713724626057_rest_7z5mjgwb9 = new actionTypes.RestAction({
    actionArgs: act_6964713724626057_rest_7z5mjgwb9_getActionArgs,
    displayName: 'deleteItem',
    events: [inst_evh_6964713724626057_deleted_auxyt6ulv],
  });
  const inst_evh_6964713724626057_clickPreventStop_qgunmdm46 =
    new actionTypes.EventHandlerImpl({
      action: act_6964713724626057_rest_7z5mjgwb9,
      event: 'click.prevent.stop',
      displayName: 'deleteItem',
    });
  const inst_evh_6964713724626057_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6964713724626057_clickPreventStop_qgunmdm46],
      event: 'click.prevent.stop',
    });
  const act_2272469684735879_script_q4571ujxc = new actionTypes.ScriptAction({
    actionArgs: act_2272469684735879_script_q4571ujxc_getActionArgs,
  });
  const inst_evh_2272469684735879_saved_t194uowgj =
    new actionTypes.EventHandlerImpl({
      action: act_2272469684735879_script_q4571ujxc,
      event: 'saved',
      displayName: 'script',
    });
  const act_2272469684735879_reloadComponentData_1x01uml4l =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2272469684735879_saved_9wf4ttvkw =
    new actionTypes.EventHandlerImpl({
      action: act_2272469684735879_reloadComponentData_1x01uml4l,
      event: 'saved',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2272469684735879_saved = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2272469684735879_saved_t194uowgj,
      inst_evh_2272469684735879_saved_9wf4ttvkw,
    ],
    event: 'saved',
  });
  const act_2272469684735879_script_q44btt2ru = new actionTypes.ScriptAction({
    actionArgs: act_2272469684735879_script_q44btt2ru_getActionArgs,
  });
  const inst_evh_2272469684735879_cancelEdit_la1e42759 =
    new actionTypes.EventHandlerImpl({
      action: act_2272469684735879_script_q44btt2ru,
      event: 'cancelEdit',
      displayName: 'script',
    });
  const inst_evh_2272469684735879_cancelEdit =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2272469684735879_cancelEdit_la1e42759],
      event: 'cancelEdit',
    });
  const act_3373384577795494_script_sd8l4uts7 = new actionTypes.ScriptAction({
    actionArgs: act_3373384577795494_script_sd8l4uts7_getActionArgs,
    displayName: 'createRule',
  });
  const inst_evh_3373384577795494_clickPreventStop_qtz6pj5sd =
    new actionTypes.EventHandlerImpl({
      action: act_3373384577795494_script_sd8l4uts7,
      event: 'click.prevent.stop',
      displayName: 'createRule',
    });
  const inst_evh_3373384577795494_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3373384577795494_clickPreventStop_qtz6pj5sd],
      event: 'click.prevent.stop',
    });
  const act_3601807637515735_script_6dfyybgot = new actionTypes.ScriptAction({
    actionArgs: act_3601807637515735_script_6dfyybgot_getActionArgs,
    displayName: 'createRule',
  });
  const inst_evh_3601807637515735_clickPreventStop_8jioyvmkj =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_script_6dfyybgot,
      event: 'click.prevent.stop',
      displayName: 'createRule',
    });
  const inst_evh_3601807637515735_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3601807637515735_clickPreventStop_8jioyvmkj],
      event: 'click.prevent.stop',
    });
  const act_3322283983417540_closeComponent_eqeclvnv6 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_3322283983417540_clickPreventStop_syyybcf5c =
    new actionTypes.EventHandlerImpl({
      action: act_3322283983417540_closeComponent_eqeclvnv6,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_3322283983417540_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3322283983417540_clickPreventStop_syyybcf5c],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_nkthxwh0g,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_it2v20k6y,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_0l2r8kdhg,
    inst_evh_8564662037462133_close,
    inst_evh_4615685607825208_clickPreventStop_b0k3muiq9,
    reload: inst_evh_4615685607825208_clickPreventStop_b0k3muiq9,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_close_5gk0cpm0a,
    inst_evh_2095089031469157_clickPreventStop_8qrjrvimo,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_5105091150304644_saved_isf98dl0y,
    inst_evh_5105091150304644_saved_k404kv0mo,
    inst_evh_5105091150304644_saved,
    inst_evh_5105091150304644_cancelEdit_7lfryjrrx,
    inst_evh_5105091150304644_cancelEdit,
    inst_evh_8387648034966287_clickPreventStop_svfhbvb3y,
    editItem: inst_evh_8387648034966287_clickPreventStop_svfhbvb3y,
    inst_evh_8387648034966287_clickPreventStop,
    inst_evh_6964713724626057_deleted_auxyt6ulv,
    inst_evh_6964713724626057_clickPreventStop_qgunmdm46,
    deleteItem: inst_evh_6964713724626057_clickPreventStop_qgunmdm46,
    inst_evh_6964713724626057_clickPreventStop,
    inst_evh_2272469684735879_saved_t194uowgj,
    inst_evh_2272469684735879_saved_9wf4ttvkw,
    inst_evh_2272469684735879_saved,
    inst_evh_2272469684735879_cancelEdit_la1e42759,
    inst_evh_2272469684735879_cancelEdit,
    inst_evh_3373384577795494_clickPreventStop_qtz6pj5sd,
    createRule: inst_evh_3373384577795494_clickPreventStop_qtz6pj5sd,
    inst_evh_3373384577795494_clickPreventStop,
    inst_evh_3601807637515735_clickPreventStop_8jioyvmkj,
    inst_evh_3601807637515735_clickPreventStop,
    inst_evh_3322283983417540_clickPreventStop_syyybcf5c,
    inst_evh_3322283983417540_clickPreventStop,
  };

  const evh_7315092382398562_close_nkthxwh0g = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_nkthxwh0g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_it2v20k6y = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_it2v20k6y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_0l2r8kdhg = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_0l2r8kdhg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_b0k3muiq9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_b0k3muiq9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_5gk0cpm0a = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_5gk0cpm0a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_8qrjrvimo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_8qrjrvimo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_saved_isf98dl0y = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_saved_isf98dl0y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_saved_k404kv0mo = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_saved_k404kv0mo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_saved = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_saved.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_cancelEdit_7lfryjrrx = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_cancelEdit_7lfryjrrx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5105091150304644_cancelEdit = async (event, scope) => {
    return await actions.inst_evh_5105091150304644_cancelEdit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8387648034966287_clickPreventStop_svfhbvb3y = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8387648034966287_clickPreventStop_svfhbvb3y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8387648034966287_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8387648034966287_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6964713724626057_deleted_auxyt6ulv = async (event, scope) => {
    return await actions.inst_evh_6964713724626057_deleted_auxyt6ulv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6964713724626057_clickPreventStop_qgunmdm46 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6964713724626057_clickPreventStop_qgunmdm46.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6964713724626057_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6964713724626057_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_saved_t194uowgj = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_saved_t194uowgj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_saved_9wf4ttvkw = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_saved_9wf4ttvkw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_saved = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_saved.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_cancelEdit_la1e42759 = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_cancelEdit_la1e42759.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2272469684735879_cancelEdit = async (event, scope) => {
    return await actions.inst_evh_2272469684735879_cancelEdit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3373384577795494_clickPreventStop_qtz6pj5sd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3373384577795494_clickPreventStop_qtz6pj5sd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3373384577795494_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3373384577795494_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop_8jioyvmkj = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop_8jioyvmkj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_clickPreventStop_syyybcf5c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3322283983417540_clickPreventStop_syyybcf5c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BillingRules"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <ItpBox :gap="2">
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :tooltip="gettext('Reload')"
            :spinning="actions.reload.isRunning"
            :text="gettext('Reload')"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            icon="lets-icons:import"
            :text="pgettext('Templates', 'Import')"
            variant="light"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            icon="lets-icons:export"
            :text="pgettext('BillingRules', 'Export')"
            variant="light"
            :disabled="!items || !items.length"
            use-auth
            force-download
            downloadFileName="billing-rules.json"
            :href="
              $fn.urlFor('export_object_by_type', {
                object_type: 'billing-rule',
              })
            "
          >
          </ItpButton>
        </ItpBox>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <BListGroup
        v-if="(items && items.length) || createItem"
        :style="b_1040167445267876_style"
      >
        <template v-for="item in items">
          <BListGroupItem :key="item.id" v-if="items && items.length">
            <ItpBox alignItems="start">
              <BillingRule
                :item="item"
                :editing="editingItemID == item.id"
                class="flex-grow-1"
                @saved="evh_5105091150304644_saved($event, { item })"
                @cancelEdit="evh_5105091150304644_cancelEdit($event, { item })"
              >
              </BillingRule>
              <div class="ms-3" :style="b_8527427120829392_style">
                <BDropdown
                  size="sm"
                  toggle-class="btn-default-borderless"
                  no-caret
                  variant="none"
                >
                  <template #button-content>
                    <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
                  </template>
                  <BDropdownItem
                    @click.prevent.stop="
                      evh_8387648034966287_clickPreventStop($event, { item })
                    "
                  >
                    <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
                  </BDropdownItem>
                  <BDropdownItem
                    @click.prevent.stop="
                      evh_6964713724626057_clickPreventStop($event, { item })
                    "
                  >
                    <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
                  </BDropdownItem>
                </BDropdown>
              </div>
            </ItpBox>
          </BListGroupItem>
        </template>
        <BListGroupItem v-if="createItem">
          <ItpBox alignItems="start">
            <BillingRule
              editing
              class="flex-grow-1"
              @saved="evh_2272469684735879_saved($event, {})"
              @cancelEdit="evh_2272469684735879_cancelEdit($event, {})"
            >
            </BillingRule>
            <div class="ms-3" :style="b_8363486085232621_style"></div>
          </ItpBox>
        </BListGroupItem>
        <BListGroupItem class="border-0" v-if="!createItem">
          <ItpBox justifyContent="center">
            <ItpButton
              variant="primary"
              :text="pgettext('BillingRules', 'Create rule')"
              :disabled="editingItemID != null"
              class="w-auto"
              @click.prevent.stop="
                evh_3373384577795494_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </ItpBox>
        </BListGroupItem>
      </BListGroup>
      <ItpBox
        direction="column"
        alignItems="center"
        justifyContent="center"
        v-if="(!items || !items.length) && !createItem"
        :style="b_4186377094414664_style"
      >
        <p>
          {{ pgettext('BillingRules', 'No rules have been defined yet.') }}
        </p>
        <ItpButton
          variant="primary"
          :text="pgettext('BillingRules', 'Create rule')"
          @click.prevent.stop="
            evh_3601807637515735_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox class="ms-auto">
          <ItpButton
            :text="gettext('Close')"
            variant="default"
            @click.prevent.stop="
              evh_3322283983417540_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcFooter>
    </template>
  </ItpPage>
</template>
